import React from 'react';

const TEAL = '#02857E';
const SLATE = '#1f2937';

type StepKey =
  | 'shipperJoins'
  | 'buildNetwork'
  | 'postFreight'
  | 'brokersCompete'
  | 'awardBid'
  | 'relationship';

interface Props {
  step: StepKey;
  className?: string;
}

const Wrap: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div
    className={`relative w-full h-full flex items-center justify-center ${className ?? ''}`}
    style={{
      background: `linear-gradient(135deg, ${TEAL}10 0%, ${TEAL}03 100%)`,
    }}
  >
    {/* subtle grid */}
    <div
      className="absolute inset-0 opacity-40"
      style={{
        backgroundImage: `linear-gradient(${TEAL}1a 1px, transparent 1px), linear-gradient(90deg, ${TEAL}1a 1px, transparent 1px)`,
        backgroundSize: '24px 24px',
      }}
    />
    {children}
  </div>
);

const StepIllustration: React.FC<Props> = ({ step, className }) => {
  switch (step) {
    case 'shipperJoins':
      return (
        <Wrap className={className}>
          <svg viewBox="0 0 200 120" className="relative w-3/4 h-3/4">
            <rect x="60" y="30" width="80" height="60" rx="10" fill="white" stroke={TEAL} strokeWidth="2" />
            <circle cx="100" cy="55" r="11" fill={TEAL} />
            <path d="M82 80 Q100 65 118 80" stroke={TEAL} strokeWidth="2.5" fill="none" strokeLinecap="round" />
            <circle cx="155" cy="42" r="14" fill={TEAL} />
            <path d="M149 42 l4 4 l8 -8" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" />
          </svg>
        </Wrap>
      );
    case 'buildNetwork':
      return (
        <Wrap className={className}>
          <svg viewBox="0 0 200 120" className="relative w-3/4 h-3/4">
            <circle cx="100" cy="60" r="13" fill={TEAL} />
            {[
              [40, 30],
              [160, 30],
              [40, 90],
              [160, 90],
              [100, 18],
              [100, 102],
            ].map(([x, y], i) => (
              <g key={i}>
                <line x1="100" y1="60" x2={x} y2={y} stroke={TEAL} strokeWidth="1.5" strokeDasharray="3 3" />
                <circle cx={x} cy={y} r="7" fill="white" stroke={TEAL} strokeWidth="2" />
              </g>
            ))}
          </svg>
        </Wrap>
      );
    case 'postFreight':
      return (
        <Wrap className={className}>
          <svg viewBox="0 0 200 120" className="relative w-3/4 h-3/4">
            <rect x="55" y="20" width="90" height="80" rx="8" fill="white" stroke={TEAL} strokeWidth="2" />
            <rect x="65" y="32" width="55" height="6" rx="3" fill={SLATE} opacity="0.85" />
            <rect x="65" y="46" width="70" height="4" rx="2" fill={SLATE} opacity="0.25" />
            <rect x="65" y="56" width="60" height="4" rx="2" fill={SLATE} opacity="0.25" />
            <rect x="65" y="66" width="68" height="4" rx="2" fill={SLATE} opacity="0.25" />
            <rect x="65" y="80" width="70" height="12" rx="4" fill={TEAL} />
            <circle cx="155" cy="25" r="10" fill="#facc15" />
            <text x="155" y="29" textAnchor="middle" fontSize="12" fontWeight="700" fill={SLATE}>
              $
            </text>
          </svg>
        </Wrap>
      );
    case 'brokersCompete':
      return (
        <Wrap className={className}>
          <svg viewBox="0 0 200 120" className="relative w-3/4 h-3/4">
            {[20, 50, 80].map((y, i) => (
              <g key={i}>
                <rect x="30" y={y} width="140" height="22" rx="6" fill="white" stroke={TEAL} strokeWidth={i === 0 ? 2.5 : 1.5} strokeOpacity={i === 0 ? 1 : 0.5} />
                <circle cx="44" cy={y + 11} r="6" fill={i === 0 ? TEAL : '#e5e7eb'} />
                <text x="44" y={y + 14} textAnchor="middle" fontSize="8" fontWeight="700" fill={i === 0 ? 'white' : SLATE}>
                  {i + 1}
                </text>
                <rect x="58" y={y + 7} width="60" height="4" rx="2" fill={SLATE} opacity="0.4" />
                <rect x="58" y={y + 14} width="40" height="3" rx="1.5" fill={SLATE} opacity="0.2" />
                <rect x="130" y={y + 7} width="30" height="10" rx="3" fill={i === 0 ? TEAL : '#e5e7eb'} />
              </g>
            ))}
          </svg>
        </Wrap>
      );
    case 'awardBid':
      return (
        <Wrap className={className}>
          <svg viewBox="0 0 200 120" className="relative w-3/4 h-3/4">
            <path d="M80 90 L120 90 L130 100 L70 100 Z" fill={TEAL} />
            <rect x="92" y="60" width="16" height="30" fill={TEAL} />
            <path
              d="M75 60 Q75 35 100 35 Q125 35 125 60 Z"
              fill="#facc15"
              stroke={TEAL}
              strokeWidth="2"
            />
            <circle cx="100" cy="20" r="6" fill={TEAL} />
            <path d="M94 20 l4 4 l8 -8" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" />
          </svg>
        </Wrap>
      );
    case 'relationship':
      return (
        <Wrap className={className}>
          <svg viewBox="0 0 200 120" className="relative w-3/4 h-3/4">
            <path
              d="M55 70 Q70 50 90 60 L110 70 Q130 50 145 70 Q150 80 140 88 L100 92 L60 88 Q50 80 55 70 Z"
              fill={TEAL}
              fillOpacity="0.9"
            />
            <circle cx="78" cy="48" r="9" fill={TEAL} />
            <circle cx="122" cy="48" r="9" fill="#facc15" />
            <path
              d="M30 100 Q100 110 170 100"
              fill="none"
              stroke={TEAL}
              strokeWidth="2"
              strokeDasharray="4 4"
              strokeLinecap="round"
            />
          </svg>
        </Wrap>
      );
  }
};

export default StepIllustration;
